import { Card, Col, message, Row } from 'antd';
import { get, includes, isArray, map } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import api from '../../../common/api';
import {
  BREAKPOINTS,
  DASHBOARD_API_ROUTES,
  DEFAULTDATETIMEFORMATFORREPORTS,
  STAGE_STATUS,
} from '../../../common/constants';
import AgencySummaryTable from './AgencySummaryTable';
import CustomRangePicker from './CustomRangePicker';
import ProjectSummaryTable from './ProjectSummaryTable';
import UserSummaryTable from './UserSummaryTable';

const Overview = ({ tenantId }) => {
  const [projectStats, setProjectStats] = useState([]);
  const [userStats, setUserStats] = useState([]);
  const [agencyStats, setAgencyStats] = useState([]);
  const [filterOption, setFilterOption] = useState([
    moment().subtract(1, 'year').format(DEFAULTDATETIMEFORMATFORREPORTS),
    moment().format(DEFAULTDATETIMEFORMATFORREPORTS),
  ]);
  const [agencyLoading, setAgencyLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [projectLoading, setProjectLoading] = useState(false);
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);

  const getProjectStats = () => {
    let query = DASHBOARD_API_ROUTES.PROJECT_LIST;
    if (isArray(filterOption) && filterOption?.length === 2) {
      query = `${query}?startDate=${filterOption?.[0]}&endDate=${filterOption?.[1]}`;
    }
    setProjectLoading(true);

    api
      .get(query)
      .then((resultSet) => {
        const table = get(resultSet, 'data.data');
        const data = [];
        const dataArray = [];

        for (let i = 0; i < table?.length; i += 1) {
          const dataItem = [];

          dataItem.push(table[i]);

          while (
            i < table.length - 1 &&
            table?.[i]?.['Project.id'] === table?.[i + 1]?.['Project.id']
          ) {
            i += 1;
            dataItem.push(table[i]);
          }

          dataArray.push(dataItem);
        }
        for (let k = 0; k < dataArray.length; k += 1) {
          let pass = 0;
          let fail = 0;
          let total = 0;
          map(dataArray[k], (value) => {
            if (value?.['EqcStageHistory.status'] === 'FAIL') {
              fail += Number(value?.['EqcStageHistory.count'] || 0);
            }
            if (value?.['EqcStageHistory.status'] === 'PASS') {
              pass += Number(value?.['EqcStageHistory.count'] || 0);
            }
            if (
              includes(
                [
                  STAGE_STATUS.APPROVAL_PENDING,
                  STAGE_STATUS.APPROVED,
                  STAGE_STATUS.BYPASS,
                  STAGE_STATUS.FAIL,
                  STAGE_STATUS.PASS,
                  STAGE_STATUS.REDO,
                  STAGE_STATUS.SKIP,
                ],
                value?.['EqcStageHistory.status'],
              )
            ) {
              total += Number(value?.['EqcStageHistory.count'] || 0);
            }
          });
          const row = {
            id: dataArray?.[k]?.[0]?.['Project.id'],
            projectName: dataArray?.[k]?.[0]?.['Project.name'],
            failCount: Number(fail),
            passCount: Number(pass),
            totalCount: Number(total),
          };
          if (row.id !== null) {
            data.push(row);
          }
        }
        setProjectStats(data);
      })
      .catch((error) => {
        if (error?.response?.data?.error?.message) {
          message.destroy();
          message.error(error?.response?.data?.error?.message);
        }
      })
      .finally(() => setProjectLoading(false));
  };

  const getUserStats = () => {
    const userId = 'User.id';
    const userName = 'User.name';
    const status = 'EqcStageHistory.status';
    const count = 'EqcStageHistory.count';
    let query = DASHBOARD_API_ROUTES.USER_LIST;
    if (isArray(filterOption) && filterOption?.length === 2) {
      query = `${query}?startDate=${filterOption?.[0]}&endDate=${filterOption?.[1]}`;
    }
    setUserLoading(true);
    api
      .get(query)
      .then((resultSet) => {
        const table = get(resultSet, 'data.data');
        const data = [];
        const userGroups = {};

        table?.forEach((record) => {
          const id = record?.[userId];
          if (id) {
            if (!userGroups[id]) {
              userGroups[id] = [];
            }
            userGroups[id].push(record);
          }
        });

        Object.keys(userGroups).forEach((id) => {
          const userRecords = userGroups[id];
          let pass = 0;
          let fail = 0;
          let total = 0;

          map(userRecords, (value) => {
            if (value?.[status] === 'FAIL') {
              fail += Number(value?.[count] || 0);
            }
            if (value?.[status] === 'PASS') {
              pass += Number(value?.[count] || 0);
            }
            if (
              includes(
                [
                  STAGE_STATUS.APPROVAL_PENDING,
                  STAGE_STATUS.APPROVED,
                  STAGE_STATUS.BYPASS,
                  STAGE_STATUS.FAIL,
                  STAGE_STATUS.PASS,
                  STAGE_STATUS.REDO,
                  STAGE_STATUS.SKIP,
                ],
                value?.[status],
              )
            ) {
              total += Number(value?.[count] || 0);
            }
          });

          const row = {
            id: userRecords?.[0]?.[userId],
            userName: userRecords?.[0]?.[userName],
            failCount: Number(fail),
            passCount: Number(pass),
            totalCount: Number(total),
          };

          if (row.id !== null) {
            data.push(row);
          }
        });

        setUserStats(data);
      })
      .catch((error) => {
        if (error?.response?.data?.error?.message) {
          message.destroy();
          message.error(error?.response?.data?.error?.message);
        }
      })
      .finally(() => setUserLoading(false));
  };

  const getAgencyStats = () => {
    const agencyId = 'Agency.id';
    const agencyName = 'Agency.name';
    const status = 'EqcStageHistory.status';
    const count = 'EqcStageHistory.count';
    let query = DASHBOARD_API_ROUTES.AGENCY_LIST;
    if (isArray(filterOption) && filterOption?.length === 2) {
      query = `${query}?startDate=${filterOption?.[0]}&endDate=${filterOption?.[1]}`;
    }
    setAgencyLoading(true);
    api
      .get(query)
      .then((resultSet) => {
        const table = get(resultSet, 'data.data');
        const data = [];

        const agencyGroups = {};

        table?.forEach((record) => {
          const id = record?.[agencyId];
          if (id) {
            if (!agencyGroups[id]) {
              agencyGroups[id] = [];
            }
            agencyGroups[id].push(record);
          }
        });

        Object.keys(agencyGroups).forEach((id) => {
          const agencyRecords = agencyGroups[id];
          let pass = 0;
          let fail = 0;
          let total = 0;

          map(agencyRecords, (value) => {
            if (value?.[status] === 'FAIL') {
              fail += Number(value?.[count] || 0);
            }
            if (value?.[status] === 'PASS') {
              pass += Number(value?.[count] || 0);
            }
            if (
              includes(
                [
                  STAGE_STATUS.APPROVAL_PENDING,
                  STAGE_STATUS.APPROVED,
                  STAGE_STATUS.BYPASS,
                  STAGE_STATUS.FAIL,
                  STAGE_STATUS.PASS,
                  STAGE_STATUS.REDO,
                  STAGE_STATUS.SKIP,
                ],
                value?.[status],
              )
            ) {
              total += Number(value?.[count] || 0);
            }
          });

          const row = {
            id: agencyRecords?.[0]?.[agencyId],
            agencyName: agencyRecords?.[0]?.[agencyName],
            failCount: Number(fail),
            passCount: Number(pass),
            totalCount: Number(total),
          };

          if (row.id !== null) {
            data.push(row);
          }
        });

        setAgencyStats(data);
      })
      .catch((error) => {
        if (error?.response?.data?.error?.message) {
          message.destroy();
          message.error(error?.response?.data?.error?.message);
        }
      })
      .finally(() => setAgencyLoading(false));
  };

  useEffect(() => {
    getProjectStats();
    getUserStats();
    getAgencyStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOption, tenantId]);

  return isDesktopViewport ? (
    <Card>
      <div className="d-flex align-center justify-between mb-15">
        <h1>Overview</h1>
        <CustomRangePicker
          setDateSelected={(date) => {
            if (date?.length > 0) {
              setFilterOption([
                moment(date?.[0]).format(DEFAULTDATETIMEFORMATFORREPORTS),
                moment(date?.[1]).format(DEFAULTDATETIMEFORMATFORREPORTS),
              ]);
            } else {
              setFilterOption('');
            }
          }}
          selectedDates={filterOption}
        />
      </div>
      <Row gutter={20} wrap={false} className="width-percent-100 align-start">
        <Col flex="1 1 1px">
          <ProjectSummaryTable loading={projectLoading} data={projectStats} />
        </Col>
        <Col flex="1 1 1px">
          <UserSummaryTable loading={userLoading} data={userStats} />
        </Col>
        <Col flex="1 1 1px">
          <AgencySummaryTable loading={agencyLoading} data={agencyStats} />
        </Col>
      </Row>
    </Card>
  ) : (
    <div className="common-card-wrapper">
      <div className="mb-15">
        <CustomRangePicker
          className="width-percent-100"
          setDateSelected={(date) => {
            if (date?.length > 0) {
              setFilterOption([
                moment(date?.[0]).format(DEFAULTDATETIMEFORMATFORREPORTS),
                moment(date?.[1]).format(DEFAULTDATETIMEFORMATFORREPORTS),
              ]);
            } else {
              setFilterOption('');
            }
          }}
          selectedDates={filterOption}
        />
      </div>
      <div className={!isDesktopViewport ? 'mb-15' : ''}>
        <ProjectSummaryTable loading={projectLoading} data={projectStats} />
      </div>
      <div className={!isDesktopViewport ? 'mb-15' : ''}>
        <UserSummaryTable loading={userLoading} data={userStats} />
      </div>
      <AgencySummaryTable loading={agencyLoading} data={agencyStats} />
    </div>
  );
};

export default Overview;
